import React from 'react';
import { ThemeResult } from '../utils/questionExtractor';

interface ThemeNavigatorProps {
  currentThemeIndex: number;
  extractResults: ThemeResult[];
  onPreviousTheme: () => void;
  onNextTheme: () => void;
  onThemeSelect: (index: number) => void;
  readOnly?: boolean;
}

const ThemeNavigator: React.FC<ThemeNavigatorProps> = ({
  currentThemeIndex,
  extractResults,
  onPreviousTheme,
  onNextTheme,
  onThemeSelect,
  readOnly = false,
}) => {
  const isFirst = currentThemeIndex === 0;
  const isLast = currentThemeIndex === extractResults.length - 1;

  return (
    <div className="px-6 py-6 md:px-8">
      <div className="space-y-6">
        <span className="badge-context">Resultaten</span>

        <div className="flex items-center gap-2 md:gap-3 w-full">
          <button
            type="button"
            onClick={onPreviousTheme}
            disabled={isFirst}
            className={`flex-shrink-0 w-10 h-10 rounded-full flex items-center justify-center transition-colors group ${
              isFirst
                ? 'border border-brand-border bg-[#D0D0D0] cursor-not-allowed'
                : 'bg-brand-blauw hover:opacity-90 cursor-pointer'
            }`}
            aria-label="Vorige resultaat"
          >
            <img
              src={`${process.env.PUBLIC_URL}/assets/arrow.svg`}
              alt=""
              className="w-4 h-4 rotate-180"
              aria-hidden="true"
            />
          </button>

          <div className="relative flex-1 min-w-0">
            <label htmlFor="theme-selector" className="sr-only">
              Kies resultaat
            </label>
            <select
              id="theme-selector"
              value={currentThemeIndex}
              onChange={(e) => onThemeSelect(Number(e.target.value))}
              className="w-full min-w-0 rounded-xl border border-brand-blauw bg-transparent px-4 py-2 pr-10 text-base lg:text-lg text-brand-blauw appearance-none"
            >
              {extractResults.map((result, index) => (
                <option key={result.id ?? `${result.page_name}-${index}`} value={index}>
                  {result.page_name}
                  {result.workflow_status === 'enqueued' ? ' (Bezig met samenvatten)' : ''}
                </option>
              ))}
            </select>
            <img
              src={`${process.env.PUBLIC_URL}/assets/arrow.svg`}
              alt=""
              className="w-4 h-4 absolute right-3 top-1/2 -translate-y-1/2 rotate-90 pointer-events-none"
              aria-hidden="true"
            />
          </div>

          <button
            type="button"
            onClick={onNextTheme}
            disabled={isLast}
            className={`flex-shrink-0 w-10 h-10 rounded-full flex items-center justify-center transition-colors group ${
              isLast
                ? 'border border-brand-border bg-[#D0D0D0] cursor-not-allowed'
                : 'bg-brand-blauw hover:opacity-90 cursor-pointer'
            }`}
            aria-label="Volgende resultaat"
          >
            <img
              src={`${process.env.PUBLIC_URL}/assets/arrow.svg`}
              alt=""
              className="w-4 h-4"
              aria-hidden="true"
            />
          </button>
        </div>

        {/* Promising editability once the questionnaire has been sent would be
            a lie: the editor is read-only from that point on. */}
        {!readOnly && (
          <div className="flex items-center gap-2 text-base text-brand-blauw">
            <img
              src={`${process.env.PUBLIC_URL}/assets/text-edit.svg`}
              alt=""
              className="w-6 h-6"
              aria-hidden="true"
            />
            <span>Bewerkbare tekst</span>
          </div>
        )}
      </div>
    </div>
  );
};

export default ThemeNavigator;
